import { type ReactNode, useRef, useState } from "react";
import {
	Platform,
	Pressable,
	View,
	type ViewStyle,
	useWindowDimensions,
} from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";
import { TooltipWrapper } from "@memorang/ui";
import { Button, Icon, IconButton, Text } from "react-native-paper";

import { Composer } from "./components/Composer";
import { ContextPanel } from "./components/ContextPanel";
import { ConversationThread } from "./components/ConversationThread";
import type { MessageFieldHandle } from "./components/MessageField/types";
import { PersonaAvatar } from "./components/PersonaAvatar";
import { ScenarioIntro } from "./components/ScenarioIntro";
import { useActiveScenario } from "./hooks/useActiveScenario";
import { useProethicsConversation } from "./hooks/useProethicsConversation";
import { useProethicsFlowStore } from "./hooks/useProethicsFlowStore";
import type { ArcStage } from "./types";

type Props = {
	onClose?: () => void;
};

const getStatusLabel = (
	hasEnded: boolean,
	isStreaming: boolean,
	arcStage: ArcStage,
	personaName: string,
) => {
	if (hasEnded) {
		return "Conversation ended";
	}
	if (isStreaming) {
		return `${personaName} is responding…`;
	}
	if (arcStage === "seeking_fix") {
		return "Ready to wrap up";
	}
	return "In conversation";
};

const Header = ({
	status,
	online,
	personaName,
	left,
	right,
}: {
	status: string;
	online: boolean;
	personaName: string;
	left?: ReactNode;
	right?: ReactNode;
}) => {
	const theme = useAppTheme();
	const onlineColor = online ? theme.colors.success.main : theme.colors.outline;

	return (
		<View
			style={{
				flexDirection: "row",
				alignItems: "center",
				justifyContent: "space-between",
				paddingHorizontal: 12,
				paddingVertical: 10,
				borderBottomWidth: 1,
				borderBottomColor: theme.colors.outlineVariant,
				backgroundColor: theme.colors.surface,
			}}
		>
			<View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
				{left}
				<PersonaAvatar size={40} />
				<View>
					<Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
						{personaName}
					</Text>
					<View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
						<View
							style={{
								width: 8,
								height: 8,
								borderRadius: 4,
								marginTop: 1,
								backgroundColor: onlineColor,
							}}
						/>
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{status}
						</Text>
					</View>
				</View>
			</View>
			{right}
		</View>
	);
};

// Paper's disabled TouchableRipple forces cursor: "auto" on web, which blocks
// the tooltip trigger's "help" cursor from inheriting. react-native-web
// renders any CSS cursor; RN's types only model "auto" | "pointer".
const helpCursorStyle =
	Platform.OS === "web"
		? ({ cursor: "help" } as unknown as ViewStyle)
		: undefined;

// The conversation only counts as started once the learner has sent a
// message; ending an empty conversation would produce an empty summative.
const EndConversationButton = ({
	hasEnded,
	hasMessages,
	personaName,
	onEnd,
}: {
	hasEnded: boolean;
	hasMessages: boolean;
	personaName: string;
	onEnd: () => void;
}) => {
	const theme = useAppTheme();

	const showTooltip = !(hasEnded || hasMessages);

	const button = (
		<Button
			mode="contained"
			icon={hasEnded ? "check" : "flag-checkered"}
			buttonColor={theme.colors.primary}
			textColor={theme.colors.onPrimary}
			disabled={hasEnded || !hasMessages}
			contentStyle={[
				{ height: 40, paddingHorizontal: 6 },
				showTooltip ? helpCursorStyle : null,
			]}
			accessibilityLabel="End conversation"
			onPress={onEnd}
		>
			{hasEnded ? "Conversation ended" : "End conversation"}
		</Button>
	);

	if (!showTooltip) {
		return button;
	}
	return (
		<TooltipWrapper
			title={`Send a message to ${personaName} first. You can end the conversation once it has started.`}
		>
			{button}
		</TooltipWrapper>
	);
};

export const ProethicsSimulationContainer = ({ onClose }: Props) => {
	const { width } = useWindowDimensions();
	const isWide = width >= 900;
	const [showContext, setShowContext] = useState(false);
	const convo = useProethicsConversation();
	const theme = useAppTheme();
	const composerRef = useRef<MessageFieldHandle>(null);

	const hasStarted = useProethicsFlowStore((s) => s.hasStarted);
	const startConversation = useProethicsFlowStore((s) => s.startConversation);
	const hasEnded = useProethicsFlowStore((s) => s.hasEnded);
	const endConversation = useProethicsFlowStore((s) => s.endConversation);
	const { scenario } = useActiveScenario();
	const personaName = scenario.persona.name;

	const status = getStatusLabel(
		hasEnded,
		convo.isStreaming,
		convo.arcStage,
		personaName,
	);

	if (!hasStarted) {
		return (
			<View style={{ flex: 1, backgroundColor: theme.colors.surface }}>
				<ScenarioIntro onStart={startConversation} />
			</View>
		);
	}

	const closeButton = onClose ? (
		<IconButton
			icon="close"
			size={22}
			iconColor={theme.colors.onSurfaceVariant}
			style={{ margin: 0 }}
			accessibilityLabel="Close"
			onPress={onClose}
		/>
	) : null;

	const headerRight = (
		<View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
			{isWide ? null : (
				<Pressable
					onPress={() => setShowContext((v) => !v)}
					style={{ padding: 8 }}
					accessibilityLabel="Toggle context"
				>
					<Icon
						source="information-outline"
						size={22}
						color={theme.colors.onSurfaceVariant}
					/>
				</Pressable>
			)}
			<EndConversationButton
				hasEnded={hasEnded}
				hasMessages={convo.transcript.length > 0}
				personaName={personaName}
				onEnd={endConversation}
			/>
		</View>
	);

	return (
		<View style={{ flex: 1, backgroundColor: theme.colors.surface }}>
			<Header
				status={status}
				online={!hasEnded}
				personaName={personaName}
				left={closeButton}
				right={headerRight}
			/>

			<View style={{ flex: 1, flexDirection: isWide ? "row" : "column" }}>
				<View style={{ flex: 1, minWidth: 0 }}>
					<ConversationThread
						messages={convo.transcript}
						isStreaming={convo.isStreaming}
						onStartConversation={() => composerRef.current?.focus()}
					/>
					{hasEnded ? (
						<View
							style={{
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "center",
								gap: 8,
								paddingVertical: 18,
								paddingHorizontal: 16,
								backgroundColor: theme.colors.surface,
								borderTopWidth: 1,
								borderTopColor: theme.colors.outlineVariant,
							}}
						>
							<Icon
								source="check-circle"
								size={18}
								color={theme.colors.success.main}
							/>
							<Text
								variant="bodyMedium"
								style={{
									color: theme.colors.onSurfaceVariant,
									textAlign: "center",
								}}
							>
								The conversation has ended. Click{" "}
								<Text
									variant="titleSmall"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									next
								</Text>{" "}
								to continue and receive feedback.
							</Text>
						</View>
					) : (
						<Composer
							ref={composerRef}
							disabled={convo.isStreaming}
							onSendText={convo.sendText}
						/>
					)}
				</View>
				{isWide ? (
					<View
						style={{
							width: 320,
							borderLeftWidth: 1,
							borderLeftColor: theme.colors.outlineVariant,
						}}
					>
						<ContextPanel />
					</View>
				) : showContext ? (
					<View
						style={{
							position: "absolute",
							top: 0,
							bottom: 0,
							right: 0,
							width: "85%",
							borderLeftWidth: 1,
							borderLeftColor: theme.colors.outlineVariant,
							elevation: 8,
						}}
					>
						<ContextPanel />
					</View>
				) : null}
			</View>
		</View>
	);
};
